import React, { useState } from "react";
import { SelectBox } from "devextreme-react/select-box";
import { Button } from "devextreme-react/button";
import DateBox from "devextreme-react/date-box";
import DataGrid, { Column, Editing } from "devextreme-react/data-grid";
import "./PreKFactors.css";

interface Trade {
  id: number;
  productType: string;
  tradeName: string;
  book: string;
  underlying: string;
  valuationDate: Date;
  period?: Date;
  endDate?: Date;
  delta?: number;
  price?: number;
  uom?: string;
  notional?: number;
  fx?: number;
  notionalEUR?: number;
  expiry?: Date;
  callPut?: string;
  strike?: number;
  vol?: string;
  rate?: string;
}

interface ResultItem {
  portfolio: string;
  value: number;
}

const PreKFactors: React.FC = () => {
  const [riskMeasure, setRiskMeasure] = useState<string>("1 day VaR (95%)");
  const [portfolio, setPortfolio] = useState<string>("CFR Oil Market Making Physical");
  const [cobDate, setCobDate] = useState<Date>(new Date());

  // Portfolio dropdown options
  const portfolioOptions: string[] = [
    "CFR Oil Market Making Physical",
    "Other Portfolio A",
    "Other Portfolio B",
  ];

  // Underlying dropdown options
  const underlyingOptions: string[] = ["BFZ", "ULA", "BRI", "XYZ"];

  // Mock trade data
  const [trades, setTrades] = useState<Trade[]>([
    {
      id: 1,
      productType: "Linear",
      tradeName: "BFZ Q4'26",
      book: "CFR Oil Market Making Physical",
      underlying: "BFZ",
      valuationDate: new Date("2025-08-29"),
      period: new Date("2026-10-01"),
      endDate: new Date("2026-10-31"),
      delta: 150,
      price: 625,
      uom: "USD",
      notional: 93750,
      fx: 1.18,
      notionalEUR: 79199.69,
    },
    {
      id: 2,
      productType: "Option",
      tradeName: "ULA Dec'26 650 call",
      book: "CFR Oil Market Making Physical",
      underlying: "ULA",
      valuationDate: new Date("2025-08-29"),
      expiry: new Date("2026-11-01"),
      callPut: "C",
      strike: 650,
      price: 625,
      vol: "20%",
      rate: "3%",
      notionalEUR: 15000,
    },
  ]);

  // Mock results (raw form)
  const results: ResultItem[] = [
    { portfolio: "Current portfolio", value: 50000 },
    { portfolio: "BFZ Q4'26", value: 20000 },
    { portfolio: "BRI Dec'26", value: 35000 },
    { portfolio: "ULA Dec'26 650 call", value: 15000 },
    { portfolio: "New portfolio", value: 60000 },
  ];

  return (
    <div className="risk-var-container">
      <h2>Pre-Trade K-Factors Analysis Tool</h2>

      {/* CoB Date */}
      <div className="section-var">
        <label>CoB:</label>
        <DateBox
          value={cobDate}
          type="date"
          displayFormat="dd/MM/yyyy"
          onValueChanged={(e: any) => setCobDate(e.value)}
          className="date-picker"
        />
      </div>

      {/* 2. Portfolio */}
      <div className="section-var spaced-section">
        <label>1. Select existing portfolio</label>
        <SelectBox
          dataSource={portfolioOptions}
          value={portfolio}
          onValueChanged={(e: any) => setPortfolio(e.value)}
          className="dropdown"
        />
      </div>

      {/* 3. Enter Trades */}
      <div className="section-var">
        <label className="with-spacing">3. Enter trade</label>
        <DataGrid
          dataSource={trades}
          keyExpr="id"
          showBorders={true}
          columnAutoWidth={true}
          rowAlternationEnabled={true}
          className="trade-grid"
        >
          <Editing
            mode="row"
            allowUpdating={true}
            allowDeleting={true}
            allowAdding={true}
          />

          {/* Dropdown for Product Type */}
          <Column
            dataField="productType"
            caption="Product Type"
            lookup={{
              dataSource: ["Linear", "Option"],
              valueExpr: "this",
              displayExpr: "this",
            }}
          />

          <Column dataField="tradeName" caption="Trade Name" />

          {/* Dropdown for Book */}
          <Column
            dataField="book"
            caption="Book"
            lookup={{
              dataSource: portfolioOptions,
              valueExpr: "this",
              displayExpr: "this",
            }}
          />

          {/* Dropdown for Underlying */}
          <Column
            dataField="underlying"
            caption="Underlying"
            lookup={{
              dataSource: underlyingOptions,
              valueExpr: "this",
              displayExpr: "this",
            }}
          />

          {/* Calendar pickers */}
          <Column
            dataField="valuationDate"
            caption="Valuation Date"
            dataType="date"
            format="dd/MM/yyyy"
          />
          <Column
            dataField="period"
            caption="Period"
            dataType="date"
            format="dd/MM/yyyy"
          />
          <Column
            dataField="endDate"
            caption="End Date"
            dataType="date"
            format="dd/MM/yyyy"
          />

          {/* Other numeric / text fields */}
          <Column dataField="delta" caption="Delta" />
          <Column dataField="price" caption="Price" />
          <Column dataField="uom" caption="UoM" />
          <Column dataField="notional" caption="Notional" />
          <Column dataField="fx" caption="FX" />
          <Column dataField="notionalEUR" caption="Notional (EUR)" />
        </DataGrid>
      </div>

      {/* 4. Run analysis */}
      <div className="section-var">
        <Button text="RUN" type="default" stylingMode="contained" />
      </div>

      {/* 5. Results */}
      <div className="section-var">
        <label>4. Results</label>
        <DataGrid
          dataSource={[
            {
              riskMeasure: riskMeasure,
              ...results.reduce((acc: Record<string, number>, cur) => {
                acc[cur.portfolio] = cur.value;
                return acc;
              }, {}),
            },
          ]}
          showBorders={true}
          columnAutoWidth={true}
          className="results-grid"
        >
          <Column dataField="riskMeasure" caption="Risk Measure" />
          {results.map((r) => (
            <Column
              key={r.portfolio}
              dataField={r.portfolio}
              caption={r.portfolio}
            />
          ))}
        </DataGrid>
      </div>
    </div>
  );
};

export default PreKFactors;
